import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /**
   * Emits a bundle quantity validation error for a specific line item.
   * @param {{ lineKey?: string, message: string }} config - Error payload.
   */
  #emitBundleQuantityError(config) {
    const message = String(config.message || '').trim();
    if (!message) return;

    this.dispatchEvent(
      new CustomEvent('bundle:quantity-error', {
        bubbles: true,
        detail: {
          lineKey: String(config.lineKey || ''),
          message,
        },
      })
    );
  }

  /**
   * Prevent delegated remove actions from bubbling into dialog close behavior.
   * @param {Event} [event] - Optional delegated DOM event.
   */
  #stopRemovalEvent(event) {
    if (!(event instanceof Event)) return;

    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Fetches the latest cart JSON.
   * @returns {Promise<any>} Current cart payload.
   */
  #fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
  }

  /**
   * Builds a cart/update payload for all bundle line item keys matching the group id.
   * @param {any} cart - Current cart payload.
   * @param {string} bundleGroupId - Shared bundle group identifier.
   * @param {number} fallbackLine - Fallback line index if no keyed updates are found.
   * @returns {Record<string, number>} Keyed quantity updates.
   */
  #buildBundleRemovalUpdates(cart, bundleGroupId, fallbackLine) {
    /** @type {Record<string, number>} */
    const updates = {};

    /** @type {Array<any>} */ (cart.items || []).forEach((item) => {
      const itemBundleGroupId = item?.properties?._bb_bundle_id;
      if (itemBundleGroupId && String(itemBundleGroupId) === bundleGroupId) {
        const key = String(item.key || '');
        if (key) {
          updates[key] = 0;
        }
      }
    });

    if (!Object.keys(updates).length && fallbackLine > 0) {
      const fallbackItem = cart.items?.[fallbackLine - 1];
      const fallbackKey = String(fallbackItem?.key || '');
      if (fallbackKey) {
        updates[fallbackKey] = 0;
      }
    }

    return updates;
  }

  /**
   * Finds all bundle line items matching a group id.
   * @param {any} cart - Current cart payload.
   * @param {string} bundleGroupId - Shared bundle group identifier.
   * @param {number} fallbackLine - Fallback line index.
   * @returns {Array<{ line: number, key: string, quantity: number }>} Bundle lines.
   */
  #findBundleLineItems(cart, bundleGroupId, fallbackLine) {
    const bundleLines = [];

    /** @type {Array<any>} */ (cart.items || []).forEach((item, index) => {
      const itemBundleGroupId = item?.properties?._bb_bundle_id;
      if (itemBundleGroupId && String(itemBundleGroupId) === bundleGroupId) {
        bundleLines.push({
          line: index + 1,
          key: String(item.key || ''),
          quantity: Number(item.quantity || 0),
        });
      }
    });

    if (!bundleLines.length && fallbackLine > 0) {
      const fallbackItem = cart.items?.[fallbackLine - 1];
      bundleLines.push({
        line: fallbackLine,
        key: String(fallbackItem?.key || ''),
        quantity: Number(fallbackItem?.quantity || 0),
      });
    }

    return bundleLines.filter((item) => item.line > 0 && item.quantity >= 0);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    const lineItemRow = this.refs.cartItemRows[line - 1];
    const bundleGroupId = lineItemRow?.dataset.bundleGroupId;
    if (bundleGroupId) {
      return;
    }

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number | { line?: number, bundleGroupId?: string }} line - The line item index.
   * @param {Event} [event] - Optional click event from delegated handler.
    * @returns {void | Promise<void>}
   */
  onLineItemRemove(line, event) {
    this.#stopRemovalEvent(event);

    if (typeof line === 'object' && line?.bundleGroupId) {
      return this.onBundleLineItemRemove(line);
    }

    const normalizedLine = typeof line === 'number' ? line : Number(line?.line);
    if (!normalizedLine) return;

    this.updateQuantity({
      line: normalizedLine,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[normalizedLine - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const drawer = this.closest('cart-drawer-component');
    const scopedTemplate = drawer?.querySelector('[data-empty-cart-template]');
    const template =
      scopedTemplate instanceof HTMLTemplateElement ? scopedTemplate : document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Handles grouped line item removal for bundle-builder items.
   * @param {{ line?: number, bundleGroupId?: string }} payload - Removal payload.
   * @param {Event} [event] - Optional click event from delegated handler.
    * @returns {void | Promise<void>}
   */
  onBundleLineItemRemove(payload = {}, event) {
    this.#stopRemovalEvent(event);

    const bundleGroupId = String(payload.bundleGroupId || '');
    const fallbackLine = Number(payload.line || 0);

    if (!bundleGroupId) {
      return this.onLineItemRemove(fallbackLine);
    }

    return this.#fetchCart()
      .then((cart) => {
        const updates = this.#buildBundleRemovalUpdates(cart, bundleGroupId, fallbackLine);

        if (!Object.keys(updates).length && fallbackLine > 0) {
          return this.updateLineItem({
            line: fallbackLine,
            quantity: 0,
            action: 'clear',
          });
        }

        return this.updateLineItems({
          updates,
          action: 'clear',
          errorLine: fallbackLine || 1,
        });
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    this.updateLineItem(config);
  }

  /**
   * Gets all cart section IDs that should be re-rendered after cart updates.
   * @returns {Set<string>} Section ids to update.
   */
  #getSectionsToUpdate() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return sectionsToUpdate;
  }

  /**
   * Handles successful cart response and updates UI sections.
   * @param {any} parsedResponseText - Parsed response from Shopify cart endpoints.
   * @param {number} errorLine - Line index used for cart error fallback.
   */
  #processCartResponse(parsedResponseText, errorLine) {
    resetShimmer(this);

    if (parsedResponseText.errors) {
      this.#handleCartError(errorLine, parsedResponseText);
      return;
    }

    const newSectionHTML = new DOMParser().parseFromString(parsedResponseText.sections[this.sectionId], 'text/html');

    // Grab the new cart item count from a hidden element
    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    // Update data-cart-quantity for all matching variants
    this.#updateQuantitySelectors(parsedResponseText);

    this.dispatchEvent(
      new CartUpdateEvent(parsedResponseText, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections: parsedResponseText.sections,
      })
    );

    morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
      mode: this.isDrawer ? 'hydration' : 'full',
    });

    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Sends a cart request and applies common UI handling.
   * @param {string} endpoint - Cart endpoint URL.
   * @param {object} payload - JSON payload.
   * @param {string} action - Analytics action name.
   * @param {number} errorLine - Line index used for cart error fallback.
   */
  #sendCartRequest(endpoint, payload, action, errorLine) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${action}:user-action`);
    const { cartTotal } = this.refs;

    this.#disableCartItems();
    cartTotal?.shimmer();

    return fetch(endpoint, fetchConfig('json', { body: JSON.stringify(payload) }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);
        this.#processCartResponse(parsedResponseText, errorLine);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates a cart line item.
   * @param {Object} config - The config.
    * @param {number} [config.line] - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} [config.action] - The action.
   * @param {number | null} [config.sellingPlanId] - The selling plan id.
    * @param {string} [config.key] - The cart line item key.
   */
  updateLineItem(config) {
    const { line, quantity, sellingPlanId, key } = config;
    const sectionsToUpdate = this.#getSectionsToUpdate();
    const normalizedLine = Number.isInteger(line) ? line : 0;

    if (normalizedLine > 0 && config.action !== 'selling-plan-change' && config.action !== 'clear') {
      const cartItemRow = this.refs.cartItemRows?.[normalizedLine - 1];
      const quantityInputCandidate = cartItemRow?.querySelector('quantity-selector-component input[ref="quantityInput"]')
        || cartItemRow?.querySelector('input[data-min-error-template]');
      const quantityInput = quantityInputCandidate instanceof HTMLInputElement ? quantityInputCandidate : null;
      const minQuantity = Number.parseInt(quantityInput?.min || '', 10);

      if (Number.isInteger(minQuantity) && minQuantity > 0 && quantity < minQuantity) {
        const minErrorTemplate = String(quantityInput?.dataset.minErrorTemplate || '').trim();
        const message = minErrorTemplate
          ? minErrorTemplate.replace('[minimum]', String(minQuantity))
          : `Minimum of ${minQuantity}`;

        this.#handleCartError(normalizedLine, { errors: message });
        return undefined;
      }
    }

    /** @type {{ line?: number, id?: string, quantity: number, sections: string, sections_url: string, selling_plan?: number | null }} */
    const payload = {
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };

    if (key) {
      payload.id = key;
    } else {
      payload.line = normalizedLine;
    }

    if (config.action === 'selling-plan-change') {
      payload.selling_plan = sellingPlanId ?? null;
    }

    return this.#sendCartRequest(`${Theme.routes.cart_change_url}`, payload, config.action || 'change', normalizedLine || 1);
  }

  /**
   * Updates the selling plan for all line items in a bundle group.
   * @param {{ bundleGroupId: string, sellingPlanId: number | null, fallbackLine?: number }} config - Update configuration.
   * @returns {Promise<void> | undefined}
   */
  updateBundleSellingPlan(config) {
    const bundleGroupId = String(config.bundleGroupId || '');
    const fallbackLine = Number(config.fallbackLine || 0);
    const targetSellingPlanId = config.sellingPlanId ?? null;

    if (!bundleGroupId) return undefined;

    const getPendingBundleLine = (cart) => {
      const bundleLines = this.#findBundleLineItems(cart, bundleGroupId, fallbackLine);
      return bundleLines.find((bundleLine) => {
        const item = cart.items?.[bundleLine.line - 1];
        const currentPlanId = item?.selling_plan_allocation?.selling_plan?.id ?? null;

        if (targetSellingPlanId === null) {
          return currentPlanId !== null;
        }

        return Number(currentPlanId) !== Number(targetSellingPlanId);
      });
    };

    let updateCount = 0;
    const maxUpdates = 20;

    const runNextUpdate = (cart) => {
      if (!cart || updateCount >= maxUpdates) {
        return Promise.resolve();
      }

      const pendingLine = getPendingBundleLine(cart);
      if (!pendingLine) {
        return Promise.resolve();
      }

      updateCount += 1;

      return this.updateLineItem({
        line: pendingLine.line,
        quantity: pendingLine.quantity,
        sellingPlanId: targetSellingPlanId,
        action: 'selling-plan-change',
      }).then(() => this.#fetchCart()).then(runNextUpdate);
    };

    return this.#fetchCart()
      .then(runNextUpdate)
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Updates one line item inside a bundle group while enforcing group minimum quantity.
   * @param {{ bundleGroupId: string, line?: number, lineKey?: string, delta?: number, quantity?: number, bundleMinQty?: number }} config
   * @returns {Promise<void> | undefined}
   */
  updateBundleLineQuantity(config) {
    const bundleGroupId = String(config.bundleGroupId || '');
    const fallbackLine = Number(config.line || 0);
    const lineKey = String(config.lineKey || '');
    const hasAbsoluteQuantity = Number.isInteger(config.quantity);
    const requestedQuantity = Number(config.quantity);
    const delta = Number(config.delta || 0);
    const requestedMinQty = Number(config.bundleMinQty || 0);

    if (!bundleGroupId) return undefined;
    if (!hasAbsoluteQuantity && (!Number.isInteger(delta) || delta === 0)) return undefined;

    return this.#fetchCart()
      .then((cart) => {
        const bundleLines = this.#findBundleLineItems(cart, bundleGroupId, fallbackLine);
        if (!bundleLines.length) {
          return undefined;
        }

        const targetLine = bundleLines.find((item) => {
          if (lineKey) {
            return item.key === lineKey;
          }

          return item.line === fallbackLine;
        });

        if (!targetLine) {
          return undefined;
        }

        const currentQty = Number(targetLine.quantity || 0);
        const nextQty = hasAbsoluteQuantity ? requestedQuantity : currentQty + delta;

        if (!Number.isInteger(nextQty) || nextQty < 0) {
          return undefined;
        }

        const bundleTotalQty = bundleLines.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
        const targetCartItem = cart.items?.[targetLine.line - 1];
        const itemMinQty = Number(targetCartItem?.properties?._bb_bundle_min_qty || 0);
        const bundleMinQty = Math.max(requestedMinQty, itemMinQty);
        const nextBundleTotalQty = bundleTotalQty - currentQty + nextQty;

        if (bundleMinQty > 0 && nextBundleTotalQty < bundleMinQty) {
          this.#emitBundleQuantityError({
            lineKey: targetLine.key,
            message: `Minimum of ${bundleMinQty}`,
          });
          return undefined;
        }

        if (nextQty === currentQty) {
          return undefined;
        }

        return this.updateLineItem({
          line: targetLine.line,
          key: targetLine.key,
          quantity: Math.max(0, nextQty),
          action: 'change',
        });
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Updates multiple cart line items at once.
    * @param {{ updates: Record<string, number>, action?: string, errorLine?: number }} config - Update configuration.
   */
  updateLineItems(config) {
    const sectionsToUpdate = this.#getSectionsToUpdate();
    const payload = {
      updates: config.updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };

    return this.#sendCartRequest(
      `${Theme.routes.cart_update_url}`,
      payload,
      config.action || 'change',
      config.errorLine || 1
    );
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const cartItemRow = this.refs.cartItemRows?.[line - 1];
    const quantityInputCandidate = cartItemRow?.querySelector('quantity-selector-component input[ref="quantityInput"]')
      || cartItemRow?.querySelector('input[data-min-error-template]');
    const quantityInput = quantityInputCandidate instanceof HTMLInputElement ? quantityInputCandidate : null;

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) {
      console.error('[CartItemsComponent] Cart item error container not found for line', line, parsedResponseText);
      return;
    }

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, {
        mode: this.isDrawer ? 'hydration' : 'full',
      });

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
